import { cn } from '@shared/utils'
import { APP_NAME } from '@shared/constants'

/**
 * Bộ nhận diện Finiva.
 *
 * Ảnh nằm ở `public/brand/`. Chữ FINIVA trong file logo màu teal (#10695B) nên
 * đọc kém trên nền tối — vì vậy trên nền tối dùng `BrandMark` (icon đặt trong ô
 * trắng) kèm chữ riêng, thay vì dùng thẳng file logo ngang.
 */

type Tone = 'onDark' | 'onLight'

interface BrandMarkProps {
  className?: string
  /** Kích thước ô chứa, mặc định theo sidebar */
  size?: 'sm' | 'md' | 'lg'
  /** Nền đặt logo lên: `onDark` cho ô trắng, `onLight` cho ô xanh nhạt */
  tone?: Tone
}

const SIZES = {
  sm: 'h-7 w-7 rounded-lg p-1',
  md: 'h-10 w-10 rounded-lg p-1.5',
  lg: 'h-14 w-14 rounded-lg p-2',
} as const

export function BrandMark({ className, size = 'sm', tone = 'onDark' }: BrandMarkProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center',
        tone === 'onDark' ? 'bg-white' : 'bg-brand-50 ring-1 ring-brand-100',
        SIZES[size],
        className,
      )}
    >
      <img
        src="/brand/icon.png"
        alt={APP_NAME}
        className="h-full w-full object-contain"
      />
    </div>
  )
}

interface BrandLockupProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  /** Dòng mô tả nhỏ dưới tên, ví dụ "Quản trị tài chính" */
  subtitle?: string
  /** Nền đặt logo lên */
  tone?: Tone
}

const TEXT_SIZES = { sm: 'text-sm', md: 'text-base', lg: 'text-lg' } as const

export function BrandLockup({
  className,
  size = 'sm',
  subtitle,
  tone = 'onDark',
}: BrandLockupProps) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <BrandMark size={size} tone={tone} />
      <div className="min-w-0">
        <span
          className={cn(
            'block truncate font-semibold tracking-wide',
            TEXT_SIZES[size],
            tone === 'onDark' ? 'text-sidebar-foreground' : 'text-brand-600',
          )}
        >
          {APP_NAME.toUpperCase()}
        </span>
        {subtitle && (
          <span
            className={cn(
              'block truncate text-2xs',
              tone === 'onDark' ? 'text-sidebar-muted' : 'text-muted-foreground',
            )}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  )
}

/** Logo đầy đủ (icon + chữ) — chỉ dùng trên nền sáng. */
export function BrandLogo({
  className,
  stacked = false,
}: {
  className?: string
  stacked?: boolean
}) {
  return (
    <img
      src={stacked ? '/brand/logo-stacked.png' : '/brand/logo.png'}
      alt={APP_NAME}
      className={cn('object-contain', className)}
    />
  )
}
